import * as pc from 'playcanvas';
import { DESTRUCTIBLE_DEFS, type DestructibleType } from '../shared';
import { matCache } from '../utils/MaterialCache';

export class DestructibleEntity {
  entity: pc.Entity;
  private hpBarBg: pc.Entity;
  private hpBarFill: pc.Entity;
  public id: string;
  public type: DestructibleType;
  private currentHp: number;
  private maxHp: number;
  private damaged: boolean = false;

  /** Server-assigned rotation in radians (used by debug wireframe). */
  public rotation: number;

  constructor(app: pc.Application, id: string, type: DestructibleType, x: number, z: number, hp: number, rotation: number = 0) {
    this.id = id;
    this.type = type;
    this.currentHp = hp;
    this.maxHp = hp;
    this.rotation = rotation;

    const def = DESTRUCTIBLE_DEFS[type];

    this.entity = new pc.Entity(`destructible_${id}`);
    app.root.addChild(this.entity);
    this.entity.setPosition(x, 0, z);

    // Main mesh
    const meshEntity = new pc.Entity('mesh');
    const shape = type === 'barrel' ? 'cylinder' : type === 'crystal_shard' ? 'cone' : 'box';
    meshEntity.addComponent('render', { type: shape });

    const hex = def.color;
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    // createUnique: emissive is mutated in updateHp() for the hit-flash effect
    const mat = matCache.createUnique({
      diffuse: new pc.Color(r, g, b),
      ...(type === 'crystal_shard' ? {
        emissive: new pc.Color(r * 0.6, g * 0.6, b * 0.6),
        emissiveIntensity: 1.5,
      } : {}),
    });
    meshEntity.render!.meshInstances[0].material = mat;

    const s = def.scale;
    meshEntity.setLocalScale(s, s, s);
    meshEntity.setLocalPosition(0, s * 0.5, 0);

    // Apply server-assigned rotation (radians → degrees, negated for PlayCanvas CCW convention)
    meshEntity.setLocalEulerAngles(0, -rotation * (180 / Math.PI), 0);

    this.entity.addChild(meshEntity);

    // HP bar
    this.hpBarBg = new pc.Entity('hpBg');
    this.hpBarBg.addComponent('render', { type: 'plane' });
    this.hpBarBg.render!.meshInstances[0].material = matCache.get({ diffuse: new pc.Color(0.15, 0.05, 0.05) });
    this.hpBarBg.render!.castShadows = false;
    this.hpBarBg.render!.receiveShadows = false;
    this.hpBarBg.setLocalScale(s * 1.2, 1, 0.12);
    this.hpBarBg.setLocalPosition(0, s + 0.4, 0);
    this.hpBarBg.enabled = false;
    this.entity.addChild(this.hpBarBg);

    this.hpBarFill = new pc.Entity('hpFill');
    this.hpBarFill.addComponent('render', { type: 'plane' });
    // createUnique: emissive color is mutated in updateHp() for color-shifting
    this.hpBarFill.render!.meshInstances[0].material = matCache.createUnique({
      diffuse: new pc.Color(0.1, 0.9, 0.3),
      emissive: new pc.Color(0.1, 0.9, 0.3),
      emissiveIntensity: 0.8,
    });
    this.hpBarFill.render!.castShadows = false;
    this.hpBarFill.render!.receiveShadows = false;
    this.hpBarFill.setLocalScale(1, 1, 0.9);
    this.hpBarFill.setLocalPosition(0, 0.002, 0);
    this.hpBarBg.addChild(this.hpBarFill);
  }

  /** Called each frame with active local bullet positions for instant HP bar reveal. */
  peekBullet(bx: number, bz: number) {
    if (this.damaged) return;
    const pos = this.entity.getPosition();
    const def = DESTRUCTIBLE_DEFS[this.type];
    const BULLET_RADIUS = 0.15;
    const halfSize = def.scale * 0.5 + BULLET_RADIUS;
    const isRound = this.type === 'barrel' || this.type === 'crystal_shard';
    let isHit: boolean;
    if (isRound) {
      const dx = bx - pos.x;
      const dz = bz - pos.z;
      isHit = dx * dx + dz * dz < halfSize * halfSize;
    } else {
      // Transform bullet into box-local space (rotate by -rotation)
      const cos = Math.cos(-this.rotation);
      const sin = Math.sin(-this.rotation);
      const rx = (bx - pos.x) * cos - (bz - pos.z) * sin;
      const rz = (bx - pos.x) * sin + (bz - pos.z) * cos;
      isHit = Math.abs(rx) < halfSize && Math.abs(rz) < halfSize;
    }
    if (isHit) {
      this.damaged = true;
      this.hpBarBg.enabled = true;
    }
  }

  update(cameraPos: pc.Vec3) {
    if (!this.damaged) return;
    const pos = this.entity.getPosition();
    const dx = cameraPos.x - pos.x;
    const dz = cameraPos.z - pos.z;
    const yaw = Math.atan2(dx, dz) * (180 / Math.PI);
    this.hpBarBg.setLocalEulerAngles(90, yaw, 0);
  }

  updateHp(hp: number) {
    const didTakeDamage = hp < this.currentHp;
    this.currentHp = hp;
    const pct = Math.max(0, hp / this.maxHp);

    if (!this.damaged && hp < this.maxHp) {
      this.damaged = true;
      this.hpBarBg.enabled = true;
    }
    this.hpBarFill.setLocalScale(pct, 1, 0.9);
    this.hpBarFill.setLocalPosition((pct - 1) * 0.5, 0.002, 0);

    // Flash red only when HP actually dropped
    if (!didTakeDamage) return;
    const mat = (this.entity.findByName('mesh') as pc.Entity | null)?.render?.meshInstances[0].material as pc.StandardMaterial | undefined;
    if (mat) {
      mat.emissive = new pc.Color(1, 0.2, 0.2);
      mat.update();
      setTimeout(() => {
        const def = DESTRUCTIBLE_DEFS[this.type];
        const hex = def.color;
        const r = parseInt(hex.slice(1, 3), 16) / 255;
        const g = parseInt(hex.slice(3, 5), 16) / 255;
        const b = parseInt(hex.slice(5, 7), 16) / 255;
        if (this.type === 'crystal_shard') {
          mat.emissive = new pc.Color(r * 0.6, g * 0.6, b * 0.6);
        } else {
          mat.emissive = new pc.Color(0, 0, 0);
        }
        mat.update();
      }, 100);
    }
  }

  destroy() {
    this.entity.destroy();
  }
}
